import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  CatButton,
  CatHeading,
  CatSkeleton
} from 'components/blocks-components';
import { Conditional } from 'components/pcc-components';
import { PriceAndAvailability } from './price-and-availability';
import {
  fetchPrice,
  fetchPriceHeadlessAndTransform
} from 'services/parts-diagram/actions';
import PartCardPrice from '../part-card-price';
import { ProductDealerPriceAvailability } from 'services/dealerPriceAvailability/types';
import useURLParams from 'hooks/useURLParams';
import styles from '../parts-overview.module.scss';
import { usePartDiagram } from 'hooks/use-part-diagram/parts-diagram-context';
import { PartsOverviewCardProps } from 'types/parts-diagram';
import { getHandleclickViewButton } from 'utils/parts-diagram';
import GetGroupPartType from 'components/group-parts-type/group-parts-type';
import PartsCheckPrice from 'components/parts-check-price/parts-check-price';
import cx from 'classnames';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';

const PartsOverviewCard: FC<PartsOverviewCardProps> = ({
  currency,
  partItem,
  handlePartView,
  onHover,
  index,
  shouldHideBtn,
  lastItem,
  handleOpen,
  openQuickViewDrawer,
  fromProductPage,
  isRTL = false,
  currencyLdFeatureFlag = false,
  hoveredItem,
  isZoomImageEnhancementFlag,
  isHeadlessPriceAvailability_additional_sec = false,
  isHighlighted,
  onPartClick,
  clickedOnPartId
}) => {
  const t = useTranslations();
  const { isCatCorp, partsDetails } = usePartDiagram();
  const [modifiedPriceData, setModifiedPriceData] =
    useState<ProductDealerPriceAvailability>();
  const [disableRow, setDisableRow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { graphics = [] } = partsDetails || {};
  const shouldDisplayDiagram = !isEmpty(graphics);

  const {
    partNumber = '',
    name = '',
    referenceNumber = '',
    ieSystemControlNumber = null,
    disambiguation = false,
    priceAndAvailabilityData
  } = partItem || {};

  const isArrangement = !!ieSystemControlNumber && !disambiguation;
  const displayCheckprice = index === 0 && isCatCorp;
  const isLastItem = index === lastItem;
  const getHandleSelectedPart = usePartDiagram();
  const getparams = useURLParams();
  const [isPartDiagramUpdate, setIsPartDiagramUpdate] = useState(false);

  const handleClick = async (partNumber) => {
    getHandleclickViewButton(partNumber, getHandleSelectedPart, getparams);
  };

  const onQuantityChange = useCallback(
    async (value: string) => {
      setDisableRow(true);
      setIsLoading(true);
      const priceData = await fetchPriceHeadlessAndTransform([
        { partNumber: partNumber, quantity: value }
      ]);

      if (priceData?.response?.priceAndAvailability) {
        setModifiedPriceData(priceData.response.priceAndAvailability[0]);
      } else {
        setModifiedPriceData(undefined);
      }
      setDisableRow(false);
      setIsLoading(false);
    },
    [partNumber, setDisableRow, setIsLoading, setModifiedPriceData]
  );

  const priceAvailabilityHeadless = useMemo(() => {
    return (
      <PriceAndAvailability
        lineItem={partItem}
        handleQuantityChange={onQuantityChange}
        modifiedPriceData={modifiedPriceData}
        disableRow={disableRow}
        isLoading={isLoading}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedPriceData, isLoading]);

  useEffect(() => {
    if (isPartDiagramUpdate) {
      unhighlightPart();
      setIsPartDiagramUpdate(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPartDiagramUpdate]);

  const priceAvailability = useMemo(() => {
    return (
      <PriceAndAvailability
        lineItem={partItem}
        handleQuantityChange={async (value) => {
          setDisableRow(true);
          setIsLoading(true);
          const PriceData = await fetchPrice([
            { partNumber: partNumber, quantity: value }
          ]);

          setModifiedPriceData(PriceData?.response?.priceAndAvailability[0]);
          setDisableRow(false);
          setIsLoading(false);
        }}
        modifiedPriceData={modifiedPriceData}
        disableRow={disableRow}
        isLoading={isLoading}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedPriceData, isLoading]);

  const priceCardSection = useMemo(() => {
    return (
      <PartCardPrice
        currency={currency}
        partItem={partItem}
        modifiedPriceData={modifiedPriceData}
        fromProductPage={fromProductPage}
        className="text-end cat-u-padding-top-xxl mx-2"
        isRTL={isRTL}
        currencyLdFeatureFlag={currencyLdFeatureFlag}
        isCatCorp={isCatCorp}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modifiedPriceData, isLoading]);

  const highlightPart = () => {
    partItem.referenceNumber !== null && onHover(partItem.referenceNumber);
  };

  const unhighlightPart = () => onHover(null);
  const listId = `part_List_id_${clickedOnPartId}`;
  return (
    <Conditional test={!!name} fallback={null}>
      <div
        className={cx('d-flex flex-column row-gap-1 cat-u-margin-bottom-sm', {
          [styles['highlighted']]: isZoomImageEnhancementFlag && isHighlighted
        })}
      >
        <div className="w-100 border-top" />
        <div className="d-flex justify-content-between align-items-center">
          <div className="px-2">
            <div
              className={cx(
                styles[
                  isZoomImageEnhancementFlag
                    ? 'partsReference'
                    : 'parts-reference-list'
                ],
                'd-flex align-items-start column-gap-2',
                isZoomImageEnhancementFlag && {
                  [styles['parts-reference--highlighted']]:
                    partItem.referenceNumber === hoveredItem,
                  [styles['parts-reference-clicked--highlighted']]:
                    isZoomImageEnhancementFlag && isHighlighted
                }
              )}
              role="none"
              onMouseEnter={highlightPart}
              onMouseLeave={unhighlightPart}
              id={listId}
            >
              <Conditional test={!!referenceNumber} fallback={null}>
                <CatButton
                  key={partItem.referenceNumber}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isZoomImageEnhancementFlag && onPartClick) {
                      onPartClick(partItem.referenceNumber);
                      setIsPartDiagramUpdate(true);
                    }
                  }}
                  variant="ghost"
                  className={cx(
                    styles['referenceNumber_button'],
                    'my-2 align-self-start',
                    styles['reference-number'],
                    {
                      [styles['reference-number--selected']]:
                        isZoomImageEnhancementFlag && isHighlighted
                    }
                  )}
                >
                  <CatHeading
                    variant="body-sm"
                    className={styles['reference-number']}
                  >
                    {referenceNumber}
                  </CatHeading>
                </CatButton>
              </Conditional>
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="cat-u-padding-right-sm my-2">
                  <GetGroupPartType
                    partNumber={partNumber}
                    name={name}
                    disambiguation={disambiguation}
                    ieSystemControlNumber={ieSystemControlNumber}
                    price={modifiedPriceData ?? priceAndAvailabilityData}
                    handleOpen={handleOpen}
                    setDrawerActive={openQuickViewDrawer}
                    catHeadingClassName={styles['partsReference__text']}
                  />
                  {displayCheckprice && (
                    <PartsCheckPrice classNames="my-2 d-block" />
                  )}
                </div>
              </div>
            </div>
            {!isArrangement &&
              !isCatCorp &&
              priceAndAvailabilityData?.enableAddToCart && (
                <div
                  className={cx(
                    { ['mx-4']: shouldDisplayDiagram },
                    'd-flex flex-column row-gap-2 cat-u-padding-top-sm'
                  )}
                >
                  {isHeadlessPriceAvailability_additional_sec
                    ? priceAvailabilityHeadless
                    : priceAvailability}
                </div>
              )}
          </div>
          <div
            className="d-flex flex-column align-self-start align-items-end"
            style={{ minWidth: 0 }}
          >
            {!ieSystemControlNumber && disambiguation && !shouldHideBtn && (
              <CatButton
                size="sm"
                onClick={() => handleClick(partItem.partNumber)}
              >
                {t('VIEW')}
              </CatButton>
            )}
            {isArrangement && !shouldHideBtn && (
              <CatButton size="sm" onClick={() => handlePartView(partItem)}>
                {t('VIEW')}
              </CatButton>
            )}
            {!disambiguation && !disableRow && priceCardSection}
            {isLoading && <CatSkeleton size="lg" />}
          </div>
        </div>
        {isLastItem && <div className="w-100 border-top" />}
      </div>
    </Conditional>
  );
};

export default React.memo(PartsOverviewCard);
